import { writeDB, api } from './store';
import { FilterTag, serializeQuery } from './filter';
import { history } from './history';
import { companyPath } from '../routes';
import { Image } from './asset';
import { useRemoteData, GetParams, PaginatedLoaderResponse, paginatedLoaderResponseFrom } from './data';
import { getCountries } from './country';
import { PaginatedResponse } from './api';
import { Membership } from './membership';
import { apollo } from './graphql';
import { graphql } from '../generated';

/* INTERFACES */
export interface BankAccount {
  name?: string | null;
  iban?: string | null;
  bic?: string | null;
  bankName?: string | null;
  vatId?: string | null;
  taxNumber?: string | null;
}

export interface Address {
  company?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  address?: string | null;
  address2?: string | null;
  postCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface CompanyBase {
  id: string;
  slug: string;
  name?: string;
  address: Address;
  bankAccount: BankAccount;
  billingEmails?: string[] | null;
  vatId?: string;
  vatNumber?: string;
  features: string[];
  invoicingCycle?: string | null;
  eventPayoutCycle?: string | null;
  lastEventPayout?: Date | null;
  defaultEventPayoutPercentage?: number | null;
  lastBookedClearingInvoice?: {
    id: string;
    reference: string;
    bookedAt: Date;
    orderId: string;
  };
  merchClearingOrderId?: string;
  merchCreditorReference?: string;
  eventCreditorReference?: string;
  $permissions: string[];
  assignableRoles?: string[];
}

export interface Company extends CompanyBase {
  icon?: Image;
}
export interface CompanyPatch extends CompanyBase {
  icon?: string | null;
}

/* COMPANIES CREATE */
export async function addCompany(company: CompanyPatch): Promise<void> {
  const response = await api.createCompany(company);
  writeDB.table('companies').insert(response.data);
  history.push(companyPath(response.data.slug));
}

/* COMPANIES READ */
export async function getRawCompanies({ page, perPage, term }: GetParams): Promise<PaginatedResponse<Company[]>> {
  const response = await api.indexCompanies({
    page: (page || 0) + 1,
    perPage,
    term,
  });
  return response as PaginatedResponse<Company[]>;
}

const COMPANY_SEARCH_QUERY = graphql(`
  query SearchCompanies($terms: [String!]!) {
    companies(terms: $terms, first: 10) {
      nodes {
        id
        name
      }
    }
  }
`);

export async function searchCompanies({ terms }: { terms: string[] }): Promise<{ id: string; name: string }[]> {
  const response = await apollo.query({ query: COMPANY_SEARCH_QUERY, variables: { terms } });
  return response.data.companies.nodes ?? [];
}

export async function getCompanies({
  page,
  perPage,
  query = [],
  sortBy = 'name',
  sortDir = 'asc',
}: {
  page: number;
  perPage: number;
  query?: FilterTag[];
  sortBy?: string;
  sortDir?: string;
}): Promise<PaginatedLoaderResponse> {
  const serverCompanies = await api.indexCompanies({
    page: (page || 0) + 1,
    perPage,
    term: typeof query === 'string' ? query : serializeQuery(query),
    sortBy,
    sortDir,
  });
  writeDB.table('companies').upsert(serverCompanies.data);
  return paginatedLoaderResponseFrom(serverCompanies);
}

export async function getRawCompany(companyId: string): Promise<PaginatedResponse<Company>> {
  const response = await api.showCompany({ companyId });
  return response as PaginatedResponse<Company>;
}

export async function getCompanyDetails(companyId: string): Promise<Company> {
  const response = await api.showCompany({ companyId });

  const company = response.data as unknown as Company;
  writeDB.table('companies').upsert(company);
  writeDB.table('memberships').upsert(
    (response.data as { memberships: Array<Partial<Membership>> }).memberships.map((mem) => {
      return { ...mem, company };
    })
  );
  return company;
}

/* COMPANIES UPDATE */
export async function patchCompany(id: string, company: Partial<CompanyPatch>): Promise<void> {
  const response = await api.updateCompany({
    companyId: id,
    ...company,
  });

  // TODO: Update api model to match local interface
  const data = response.data as unknown as Company;
  writeDB.table('companies').update(id, data);
}

export function addressPresence(address: Address | null): Address | null {
  if (!address) {
    return null;
  }
  if (
    [address.company, address.firstName, address.lastName, address.address, address.postCode, address.city].filter(
      Boolean
    ).length > 0
  ) {
    return address;
  }
  return null;
}

export function useAddressLines(address: Address): string[] {
  const [countries] = useRemoteData(getCountries, (db) => {
    return db.table('countries').all;
  });

  function renderCountry(code?: string): string | undefined {
    if (countries && countries.length > 0) {
      const country = countries.find((item) => item.countryCode === code);
      if (country) {
        return country.name;
      }
    }
    return code;
  }

  return [
    address.company,
    [address.firstName, address.lastName].filter(Boolean).join(' '),
    address.address,
    address.address2,
    [address.postCode, address.city].filter(Boolean).join(' '),
    [address.countryCode && renderCountry(address.countryCode)],
  ].filter(Boolean) as string[];
}
